import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import {
  useUpdateBillingAccountMutation,
  useUpdatePaymentMethodMutation,
} from './graphql/UpdatePaymentMethod.generated';

/**
 * Depending on the feature flag, this will either:
 * - Update a Billing Account through monograph-svc.
 * - Update both Billing Accounts and Payment Method Card through payments-svc.
 */
export function useUpdatePaymentMethod(props: UpdatePaymentMethodProps) {
  const { onSuccess } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const shouldUsePaymentMethodOperations = useFeatureFlag(
    'CELS-1109-use-payment-svc-for-payment-method-operations',
  );

  // ─── Billing Account Update ──────────────────────────────────────────

  const [updateBillingAccountResponse, updateBillingAccountMutation] =
    useUpdateBillingAccountMutation();

  const updateBillingAccount = useCallback(
    async (payload: UpdatePaymentMethodPayload) => {
      const response = await updateBillingAccountMutation({
        input: castPayloadToBillingAccount(payload),
      });

      const result = response.data?.updateBillingAccount?.billingAccount;
      const isSuccess = result?.__typename === 'BillingAccount';

      return { isSuccess };
    },
    [updateBillingAccountMutation],
  );

  // ─── Payment Method Card Update ──────────────────────────────────────

  const [updatePaymentMethodCardResponse, updatePaymentMethodCardMutation] =
    useUpdatePaymentMethodMutation();

  const updatePaymentMethodCard = useCallback(
    async (payload: UpdatePaymentMethodPayload) => {
      if (payload.paymentMethodType === 'BILLING_ACCOUNT') {
        const { isSuccess } = await updateBillingAccount(payload);

        if (!isSuccess || !shouldUsePaymentMethodOperations) {
          return { isSuccess };
        }
      }

      const response = await updatePaymentMethodCardMutation({
        input: castPayloadToPaymentMethodCard(payload),
      });

      const result = response.data?.updatePaymentMethod;
      const isSuccess = result?.__typename === 'PaymentMethodCard';

      return { isSuccess };
    },
    [
      shouldUsePaymentMethodOperations,
      updateBillingAccount,
      updatePaymentMethodCardMutation,
    ],
  );

  // ─── Hybrid Payment Method Update ────────────────────────────────────

  const updatePaymentMethod = useCallback(
    async (payload: UpdatePaymentMethodPayload) => {
      const { isSuccess } = await updatePaymentMethodCard(payload);

      if (isSuccess) {
        track('payment_credit.save_card');
        addNoticeBanner({
          text: t('credit-card-form.update.success'),
          palette: 'success',
        });

        onSuccess();

        return;
      }

      const userError = t('credit-card-form.error.save');

      track('payment_credit.save_card_failed', { userError });
      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [t, track, addNoticeBanner, updatePaymentMethodCard, onSuccess],
  );

  return {
    updatePaymentMethod,
    isUpdating:
      updatePaymentMethodCardResponse.fetching ||
      updateBillingAccountResponse.fetching,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UpdatePaymentMethodProps = Readonly<{
  onSuccess: () => void;
}>;

type UpdatePaymentMethodPayload = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
  paymentMethodType: 'PAYMENT_METHOD_CARD' | 'BILLING_ACCOUNT';
}>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function castPayloadToPaymentMethodCard(payload: UpdatePaymentMethodPayload) {
  return {
    id: payload.id,
    nickname: payload.nickname,
    isDefault: payload.isDefault,
  };
}

function castPayloadToBillingAccount(payload: UpdatePaymentMethodPayload) {
  return {
    id: Number(payload.id),
    nickname: payload.nickname,
    isDefault: payload.isDefault,
  };
}
